import { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../../_redux/user-assignment/UserAssignmentAction";
import {
  copy,
  DeleteButton,
  ExportButton,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  noRecordsAlert,
  print,
  redirectToPages,
  removalAlert,
  resultAlert
} from "../../../../../../_helpers/Helpers";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../../_metronic/_partials/controls";
import { StudentUsersFilter } from "./student-users-filter/StudentUsersFilter";
import { StudentUsersTable } from "./student-users-table/StudentUsersTable";
import { useStudentUsersUIContext } from "./StudentUsersUIContext";

export function StudentUsersCard() {
  // Student Users Card UI Context
  const studentUsersUIContext = useStudentUsersUIContext();
  const studentUsersUIProps = useMemo(() => {
    return {
      ids: studentUsersUIContext.ids,
      setIds: studentUsersUIContext.setIds,
      queryParams: studentUsersUIContext.queryParams
    };
  }, [studentUsersUIContext]);

  // Getting curret state of UserAssignment from store (Redux)
  const { currentState, currentActiveTerm } = useSelector(
    (state) => ({
      currentState: state.userAssignment,
      currentActiveTerm: state.auth.user.currentActiveTerm
    }),
    shallowEqual
  );  
  const { actionsLoading, isSuccess, type, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized, isDeletionAuthorized } = getAuthorities(authorities);

  // Student Users Redux state
  const dispatch = useDispatch();
    
  // Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  /** Belli Bir Öğrenci Kullanıcı ve Kullanıcı Grubu Listesini Excele Aktarır */
  const exportStudentUsersToExcel = () => {
    dispatch(actions.getAllStudentUserAndUserGroups(studentUsersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [30, 30, 30, 30, 30];
      const columns = ["TC Kimlik No", "Adı", "Soyadı", "Şube", "Kullanıcı Grubu"];
        
      entities.forEach(entity => rows.push([
        entity.tcKimlikNo,
        entity.name,
        entity.surname,
        entity.section,
        entity.userGroup
      ]));
        
      const filename = currentActiveTerm + " - " + (currentActiveTerm + 1) + " Eğitim Öğretim Yılı Öğrenci Kullanıcı ve Kullanıcı Grubu Listesi";
      const sheetname = "Öğrenci Kullanıcı Listesi";
      exportToExcel({ columns, rows, widths, filename, sheetname });
    });
  }

  /** Belli Bir Öğrenci Kullanıcı ve Kullanıcı Grubu Listesini PDF e Aktarır */
  const exportStudentUsersToPDF = () => {
    dispatch(actions.getAllStudentUserAndUserGroups(studentUsersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [ 
        {
          header: "TC Kimlik No",
          rows: entities.map(entity => entity.tcKimlikNo)
        },
        {
          header: "Adı",
          rows: entities.map(entity => entity.name)
        },
        {
          header: "Soyadı",
          rows: entities.map(entity => entity.surname)
        },
        {
          header: "Şube",
          rows: entities.map(entity => entity.section)
        },
        {
          header: "Kullanıcı Grubu",
          rows: entities.map(entity => entity.userGroup)
        }
      ];
    
      const filename = currentActiveTerm + " - " + (currentActiveTerm + 1) + " Eğitim Öğretim Yılı Öğrenci Kullanıcı ve Kullanıcı Grubu Listesi";
      exportToPDF(views, filename);
    });
  }
  
  /** Belli Bir Öğrenci Kullanıcı ve Kullanıcı Grubu Listesini Yazdırma Penceresini Açar */
  const printStudentUsers = () => {
    dispatch(actions.getAllStudentUserAndUserGroups(studentUsersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const header = currentActiveTerm + " - " + (currentActiveTerm + 1) + " Eğitim Öğretim Yılı Öğrenci Kullanıcı ve Kullanıcı Grubu Listesi";
      const properties = ["TC Kimlik No", "Adı", "Soyadı", "Şube", "Kullanıcı Grubu"];
    
      entities.forEach(entity => {
        data.push({
          "TC Kimlik No": entity.tcKimlikNo,
          "Adı": entity.name,
          "Soyadı": entity.surname,
          "Şube": entity.section,
          "Kullanıcı Grubu": entity.userGroup
        });
      });
    
      print({ data, properties, header });
    });    
  }
  
  /** Belli Bir Öğrenci Kullanıcı ve Kullanıcı Grubu Listesini Panoya Kopyalar */
  const copyStudentUsers = () => {
    dispatch(actions.getAllStudentUserAndUserGroups(studentUsersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const columns = ["TC Kimlik No", "Adı", "Soyadı", "Şube", "Kullanıcı Grubu"];
      const header = currentActiveTerm + " - " + (currentActiveTerm + 1) + " Eğitim Öğretim Yılı Öğrenci Kullanıcı ve Kullanıcı Grubu Listesi";
      entities.forEach(entity => rows.push([
        entity.tcKimlikNo,
        entity.name,
        entity.surname,
        entity.section,
        entity.userGroup
      ]));
      copy({ header, columns, rows });
    });
  }

  /** Belli Bir Kullanıcı Listesini İlgili Kullanıcı Gruplarından Çıkarır */
  const removeUsers = (ids) => {
    if (typeof ids === 'string') ids = ids.split(",");
    
    if (ids.length < 1) {
      noRecordsAlert();
      return;
    }

    const message = "İlgili kullanıcı(lar) kullanıcı grubu(ları)ndan çıkarılacaktır."
    removalAlert(message).then(result => {
      if (result.isConfirmed) dispatch(actions.removeUsers(ids)).then(result => onResult(result));
    });
  }
  
  const onResult = ({ isSuccess, type, message }) => {
    resultAlert(type, message);
    
    if (isSuccess) {
      setTimeout(() => {
        studentUsersUIProps.setIds([]);
        dispatch(actions.getStudentUserAndUserGroups(studentUsersUIProps.queryParams));
      }, 2500);
    }
  };

  const title = <>Öğrenci Kullanıcı Listesi{studentUsersUIProps.ids.length > 0 ?
                  <> [Seçilen Kayıt Sayısı: <span style={{color:"red"}}>{studentUsersUIProps.ids.length}</span>]</> :
                   ""}
                </>;

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title={title}>
        {
          (isExportationAuthorized || isDeletionAuthorized) &&
          <CardHeaderToolbar>
            {
              isExportationAuthorized &&
              <ExportButton
                exportToExcel={exportStudentUsersToExcel}
                exportToPDF={exportStudentUsersToPDF}
                print={printStudentUsers}
                copy={copyStudentUsers}
              />
            }
            {
              isDeletionAuthorized &&
              <DeleteButton
                path="Navigation/Close"
                title="Seçilenleri Çıkar"
                method={() => removeUsers(studentUsersUIProps.ids)}
              />
            }
          </CardHeaderToolbar>
        }
      </CardHeader>
      <CardBody>
        <StudentUsersFilter />
        <StudentUsersTable
          removeUsers={removeUsers}
        />
      </CardBody>
    </Card>
  );
}